/**
 * "This is what the tech sees" on the new job page (Corbin 10/28: "neither the
 * tech nor the office needs to misunderstand what's going on with the job").
 * It IS the iPad's block: the same component (`TechPlanBlock`) fed the tech's
 * own view of the plan (`GET /jobs/:id/tech-plan?as=tech`). Edit opens the
 * office's editor right here (`OfficeTechPlanCard`, the one place the plan is
 * written); a change after the job is on the tech's day shows on his iPad as
 * "Changed by the office".
 */
import { useState } from "react";
import { useTechPlan } from "@/lib/tech-plan";
import { InfoButton } from "@/components/info-button";
import { TechPlanBlock } from "@/components/tech-plan/tech-plan-block";
import { OfficeTechPlanCard } from "@/components/tech-plan/office-tech-plan";
import { FRESH_HELP } from "./fresh-help";

export function TechSees({ jobId, jobStatus }: { jobId: string; jobStatus: string | null | undefined }) {
    const q = useTechPlan(jobId, { asTech: true });
    const [edit, setEdit] = useState(false);
    const plan = q.data?.audience === "tech" ? q.data.plan : null;
    return (
        <section
            className="grid gap-2 rounded-2xl border-2 border-foreground/70 bg-card px-4 py-3 text-card-foreground"
            aria-label="This is what the tech sees"
            data-testid="fresh-tech-sees"
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="m-0 text-[19px] font-extrabold">This is what the tech sees</h2>
                <span className="flex items-center gap-2">
                    <button
                        type="button"
                        className="min-h-[40px] rounded-lg border border-border px-3 font-semibold hover:bg-muted"
                        onClick={() => setEdit((e) => !e)}
                        aria-expanded={edit}
                    >
                        {edit ? "Close" : "Edit"}
                    </button>
                    <InfoButton topic={FRESH_HELP.techSees} />
                </span>
            </div>
            <p className="m-0 text-[14px] text-muted-foreground">Word for word, the first thing on his iPad.</p>
            {edit ? (
                <OfficeTechPlanCard jobId={jobId} jobStatus={jobStatus} />
            ) : q.isLoading ? (
                <p className="m-0 text-muted-foreground">Loading…</p>
            ) : plan ? (
                <TechPlanBlock plan={plan} preview />
            ) : (
                <p className="m-0 text-muted-foreground">Nothing written for the tech yet. Use Edit.</p>
            )}
        </section>
    );
}
